import { Box, Button, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { MainBorderRadius, TableBgcolor, TableHeader } from '../../../app/AppStyles';
import { SearchKeyFilter } from '../../../app/AppUtils';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import SchoolclassGroupEditCard from './SchoolclassGroupEditCard';
import { deleteSchoolclassGroup, fetchSchoolclassGroups, resetSchoolclassGroupStatus, setSelectedSchoolclassGroup } from './schoolclassGroupSlice';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';

export default function SchoolclassGroupTab() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [openNew, setOpenNew] = useState(false);
    const [filter, setFilter] = useState("");

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { schoolclassGroups, schoolclassGroupsDelete } = useSelector((state) => state.management.schoolclassGroup);

    useEffect(() => {
        dispatch(fetchSchoolclassGroups());
    }, [dispatch]);

    // Delete status callback
    useEffect(() => {
        if (schoolclassGroupsDelete.status === "success") {
            dispatch(resetSchoolclassGroupStatus());
            dispatch(fetchSchoolclassGroups());
        } else if (schoolclassGroupsDelete.status === "failed") {
            dispatch(resetSchoolclassGroupStatus());
        };
    }, [dispatch, schoolclassGroupsDelete.status]);

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["schoolclassGroupName", "schoolclassGroupDescription"], filter);
    }

    const newClick = () => {
        dispatch(setSelectedSchoolclassGroup({ data: null, status: null }));
        setOpenNew(true);
    };

    const callbackModalNew = () => {
        setOpenNew(false);
        setFilter("");
        dispatch(resetSchoolclassGroupStatus());
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - schoolclassGroups.data?.filter(handleFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const handleSelectClick = (schoolclassGroup) => {
            dispatch(setSelectedSchoolclassGroup({ data: schoolclassGroup, status: "success" }));
            navigate(`/organisatiebeheer/klasgroep/${schoolclassGroup.schoolclassGroupId}`);
        };


        const handleDeleteClick = (schoolclassGroupId) => {
            dispatch(deleteSchoolclassGroup(schoolclassGroupId));
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? schoolclassGroups.data?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schoolclassGroups.data?.filter(handleFilterResult)).map(item =>
                        <TableRow key={item.schoolclassGroupId} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">
                                <Link to={`/organisatiebeheer/klasgroep/${item.schoolclassGroupId}`} style={{ color: 'black' }}>
                                    {item.schoolclassGroupName}
                                </Link>
                            </TableCell>
                            <Tooltip title={item.schoolclassGroupDescription}>
                                <TableCell align="left" sx={{
                                    whiteSpace: "nowrap",
                                    width: "50%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>{item.schoolclassGroupDescription ? item.schoolclassGroupDescription : "-"}</TableCell>
                            </Tooltip>
                            <TableCell align="center" padding="checkbox">{item.linkedSchoolclasses}</TableCell>
                            <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${item.schoolclassGroupName} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => handleDeleteClick(item.schoolclassGroupId)} />
                            </TableCell>
                            <TableCell align="center" padding="checkbox">
                                <IconButton onClick={() => handleSelectClick(item)}><ArrowForwardIosIcon color="secondary" /></IconButton>
                            </TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? schoolclassGroups.data?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schoolclassGroups.data?.filter(handleFilterResult)).length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[5, 10, 25,
                                {
                                    label: schoolclassGroups.data?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: schoolclassGroups.data?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={schoolclassGroups.data?.filter(handleFilterResult)?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <ContentLoader
                successCondition={schoolclassGroups.status === "success"}
                errorCondition={schoolclassGroups.status === "failed"}
                content={
                    <Stack sx={{ display: 'flex', flex: '1 1 auto', bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                            <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                            <Box>
                                <Button variant="outlined" onClick={newClick} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                            </Box>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DESCRIPTION}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.CLASSES}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    </Stack >
                }
            />

            <Modal open={openNew} onClose={callbackModalNew}>
                <>
                    <SchoolclassGroupEditCard handleClose={callbackModalNew} addNew />
                </>
            </Modal >
        </>
    );
}
